<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useCategoryEditorContext } from '@/composition/catalog/use-category-editor';
  import { useReportingOptions } from '@/composition/catalog/use-reporting-options';
  import { popularControlsVisible } from '@/domain/catalog/popular-showcase';
  import { ILanguage } from '@/interfaces/ILanguage';
  import { vueData } from '@/VueData';
  import V2CatalogEditorSection from '../shared/V2CatalogEditorSection.vue';
  import V2CatalogPopularRow from '../shared/V2CatalogPopularRow.vue';
  import V2CatalogReportingField from '../shared/V2CatalogReportingField.vue';
  import V2CatalogSettingsDisclosure from '../shared/V2CatalogSettingsDisclosure.vue';
  import V2CatalogTranslationsSettings from '../shared/V2CatalogTranslationsSettings.vue';

  /**
   * «Más ajustes» of a category (plan §5.15): «Traducciones» (once it
   * exists), «Categoría de reportes» (when the store has them) and «Lo más
   * pedido» (once it exists, while the storefront shows a ranking).
   */
  const editor = useCategoryEditorContext();
  const { data, access } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string) => String(proxy.$t(key));

  const { leaves } = useReportingOptions(data.restaurant.value?.id ?? null);

  const form = computed(() => editor.form.value);
  const category = computed(() => editor.category.value);
  const stringId = computed(() => data.restaurant.value?.stringId ?? null);

  const languages = computed<ILanguage[]>(
    () => vueData.restaurantLanguages ?? []
  );
  const translationsSummary = computed(() =>
    languages.value.length
      ? languages.value.map((language) => language.name).join(', ')
      : t('menuPage.translations.noLanguagesShort')
  );
  const showTranslations = computed(
    () =>
      !editor.isNew &&
      (access.value.canTranslate ||
        access.value.upsellTranslate ||
        access.value.readTranslations)
  );
  const showPopular = computed(
    () =>
      !editor.isNew &&
      popularControlsVisible({
        branches: data.restaurant.value?.branches,
        showPopular: data.restaurant.value?.settings?.showPopular,
      })
  );

  function setPopularShown(shown: boolean) {
    editor.update('popularHidden', !shown);
  }
</script>

<template>
  <V2CatalogEditorSection
    v-if="form && (showTranslations || leaves.length || showPopular)"
    class="v2-catalog-category-more-section"
    :title="t('menuPage.editor.moreSettings')"
    flush
  >
    <V2CatalogSettingsDisclosure
      v-if="showTranslations"
      :title="t('menuPage.translations.title')"
      :summary="translationsSummary"
      :upsell="!access.readTranslations"
      :string-id="stringId"
    >
      <V2CatalogTranslationsSettings
        v-if="category && data.restaurant.value"
        entity="category"
        :restaurant-id="data.restaurant.value.id"
        :source="category"
        :sizes="[]"
        :size-option-id="null"
        :has-new-sizes="false"
        :can-edit="access.canTranslate"
        :submitted="editor.submitted.value"
        :register="editor.registerExtra"
      />
    </V2CatalogSettingsDisclosure>

    <div v-if="leaves.length" class="more-reporting">
      <V2CatalogReportingField
        :value="form.reportingCategoryId"
        :leaves="leaves"
        :disabled="editor.readOnly.value"
        @input="editor.update('reportingCategoryId', $event)"
      />
    </div>

    <V2CatalogPopularRow
      v-if="showPopular"
      :value="!form.popularHidden"
      :body="t('menuPage.popular.categoryBody')"
      :disabled="editor.readOnly.value"
      @input="setPopularShown"
    />
  </V2CatalogEditorSection>
</template>

<style scoped lang="scss">
  .v2-catalog-category-more-section {
    .more-reporting {
      @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-px-4 tw-py-3;

      &:first-child {
        @apply tw-border-t-0;
      }
    }
  }
</style>
